import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Icon,
  IconList,
  Search,
  Select,
} from '@nazka/nazka.mapframe.components';
import {
  faArrowsMinimize, faBus, faMapMarkerAlt, faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useLoginModal } from '@services/hooks.service';
import { useVacancyCount, useAddressSugestions } from '@services/query.service';
import { setAllVacancies, setHomeAddress, setSearchGeometry } from '@App/components/Map/map.slice';
import {
  updateSearchType, updateDistance, updateDuration, updateTransportType, updateTransportDateTime,
} from '@App/pages/Filter/filter.slice';
import Nav from '@App/components/Nav/Nav';
import Footer from '@App/components/Footer/Footer';
import Partners from '@App/components/Partners/Partners';
import { getVacancies } from '@services/vacancy.service';
import { getTransportMode, shouldUseDateForTransportType } from '@services/utils.service';
import { setShowContactModal } from '@App/app.slice';

import TextToggle from './TextToggle/TextToggle';
import TransportSelect from './TransportSelect/TransportSelect';
import DateTimeSelect from './DateTimeSelect/DateTimeSelect';

import './home.styl';

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setShowLoginModal } = useLoginModal();

  const homeAddress = useSelector(state => state.map.homeAddress);
  const searchType = useSelector(state => state.filter.searchType);
  const duration = useSelector(state => state.filter.duration);
  const distance = useSelector(state => state.filter.distance);
  const transportType = useSelector(state => state.filter.transportType);
  const transportDateTime = useSelector(state => state.filter.transportDateTime);

  const [addressSearchValue, setAddressSearchValue] = useState('');
  const [formNotCompleted, setFormNotCompleted] = useState(false);
  const [error, setError] = useState('');

  const {
    data: vacanciesCount,
    isLoading: vacanciesCountLoading,
  } = useVacancyCount();

  const {
    data: addressSugestions,
    isLoading: addressSugestionsLoading,
  } = useAddressSugestions(addressSearchValue);

  const useDate = shouldUseDateForTransportType(transportType);

  const { mutate, isPending } = useMutation({
    mutationFn: async (params) => {
      const { vacancies, searchGeometry } = await getVacancies(params.locationDetails, params.mode, params.time);

      if (!vacancies?.length) {
        setError('Er werden geen vacatures gevonden.');
      } else {
        dispatch(setAllVacancies(vacancies));
        dispatch(setSearchGeometry(searchGeometry));

        navigate('/vacatures');
      }
    },
    onError: () => {
      setError('Er ging iets mis.');
    },
  });

  return (
    <div className="home">
      <Nav setShowLoginModal={v => setShowLoginModal(v)} />
      <div className="home__hero-section">
        <div className={`home__hero-section__form${formNotCompleted && !homeAddress ? ' home__hero-section__form--not-completed' : ''}`}>
          <h1 className="home__hero-section__form__title">
            Jouw ideale job is dichterbij dan je denkt. We helpen je op weg.
          </h1>
          <h3 className={`home__hero-section__form__subtitle${vacanciesCountLoading ? ' home__hero-section__form__subtitle--loading' : ''}`}>
            {'Er zijn momenteel '}
            <span>
              {vacanciesCount && new Intl.NumberFormat('fr-BE').format(vacanciesCount)}
            </span>
            {' vacatures in Vlaanderen.'}
          </h3>
          <Search
            className="home__hero-section__form__search"
            placeholder="Wat is je adres?"
            options={addressSugestions || []}
            onSearch={v => setAddressSearchValue(v)}
            onSelect={(s) => {
              dispatch(setHomeAddress(s));
              setFormNotCompleted(false);
              window.gtag?.('event', 'home_select_address', {
                event_category: 'Home',
                event_label: 'Home select address',
                address: s,
              });
            }}
            clear
            clearSearchInputOnBlur
            isLoading={addressSugestionsLoading}
            searchIcon={faMapMarkerAlt}
            value={homeAddress ? { id: homeAddress?.id, label: homeAddress?.place_name } : null}
          />
          {/* <Search
          // value=""
          className="home__hero-section__form__search"
          placeholder="Zoek op beroep, diploma of jobdomein (optioneel)"
          options={[]}
          onSelect={() => null}
          searchIcon={faFilterList}
        /> */}
          <fieldset className={`home__hero-section__form__fieldset home__hero-section__form__fieldset--${searchType}`}>
            <TextToggle
              className="home__hero-section__form__fieldset__toggle"
              leftText="Reistijd"
              rightText="Afstand"
              active={searchType === 'duration'}
              setActive={(v) => {
                dispatch(updateSearchType(v ? 'duration' : 'distance'));
                window.gtag?.('event', 'home_toggle_calcultaion_type', {
                  event_category: 'Home',
                  event_label: 'Home toggle calculation type',
                  calculation_type: v ? 'duration' : 'distance',
                });
              }}
            />
            <Select
              css={{ display: searchType !== 'distance' && 'none' }}
              className="home__hero-section__form__fieldset__select"
              options={[
                { id: '2', label: '2 km' },
                { id: '5', label: '5 km' },
                { id: '10', label: '10 km' },
                { id: '15', label: '15 km' },
              ]}
              value={distance}
              onInputChange={({ id }) => {
                dispatch(updateDistance(id));
                if (id) {
                  window.gtag?.('event', 'home_set_distance', {
                    event_category: 'Home',
                    event_label: 'Home set distance',
                    distance: id,
                  });
                }
              }}
              width="120px"
            />
            <Select
              css={{ display: searchType !== 'duration' && 'none' }}
              className="home__hero-section__form__fieldset__select"
              options={[
                { id: '15', label: '15 min' },
                { id: '30', label: '30 min' },
                { id: '45', label: '45 min' },
                { id: '60', label: '60 min' },
              ]}
              value={duration}
              onInputChange={({ id }) => {
                dispatch(updateDuration(id));
                if (id) {
                  window.gtag?.('event', 'home_set_duration', {
                    event_category: 'Home',
                    event_label: 'Home set duration',
                    distance: id,
                  });
                }
              }}
              width="120px"
            />
            <TransportSelect
              searchType={searchType}
              transportType={transportType}
              setTransportType={(v) => {
                dispatch(updateTransportType(v));
                if (v) {
                  window.gtag?.('event', 'home_set_transport-type', {
                    event_category: 'Home',
                    event_label: 'Home set transport type',
                    transport_type: v,
                  });
                }
              }}
            />
            {searchType === 'duration' && useDate && (
            <DateTimeSelect 
              dateTime={transportDateTime}
              setDateTime={v => dispatch(updateTransportDateTime(v))}
            />
            )}
            {error && (
            <p className="home__hero-section__form__error">
              {error}
            </p>
            )}
            <Button
              className="home__hero-section__form__fieldset__button"
              onClick={() => {
                if (!homeAddress) {
                  setFormNotCompleted(true);
                  document.querySelector('.home__hero-section__form__search__input').focus();
                } else {
                  const params = {
                    locationDetails: {
                      type: searchType,
                      lat: homeAddress.center[1],
                      lng: homeAddress.center[0],
                      radius: searchType === 'distance' ? distance : null,
                      cutoff: searchType === 'duration' ? Number(duration) * 60 : null,
                    },
                    mode: getTransportMode(transportType),
                  };

                  if (useDate && transportDateTime) {
                    const timeObj = dayjs(transportDateTime);
                    const date = timeObj.format('YYYY-MM-DD');
                    const time = timeObj.format('HH:mm:ss');
                    params.time = `${date}T${time}Z`;
                  }

                  mutate(params);
                }
              }}
              isLoading={isPending}
            >
              Zoeken
            </Button>
          </fieldset>
        </div>
      </div>
      <div className="home__content-block home__content-block--gradient">
        <div className="home__content-block__wrapper">
          <div className="home__content-block__wrapper__three">
            <div 
              className={'home__content-block__wrapper__three__item'
              + ' home__content-block__wrapper__three__item--job'}
            >
              <div className="home__content-block__wrapper__three__item__img" />
              <div className="home__content-block__wrapper__three__item__text">
                <h5>Ik zoek een job</h5>
                <p>Vind jobs in je buurt</p>
              </div>
            </div>
            <div 
              className={'home__content-block__wrapper__three__item'
              + ' home__content-block__wrapper__three__item--jobcoach'}
            >
              <div className="home__content-block__wrapper__three__item__img" />
              <div className="home__content-block__wrapper__three__item__text">
                <h5>Ik ben jobcoach</h5>
                <p>Help je klanten om bereikbare jobs te vinden</p>
              </div>
            </div>
            <div 
              className={'home__content-block__wrapper__three__item'
              + ' home__content-block__wrapper__three__item--employer'}
            >              
              {' '}
              <div className="home__content-block__wrapper__three__item__img" />
              <div className="home__content-block__wrapper__three__item__text">
                <h5>Ik ben werkgever</h5>
                <p>Zet je mobiliteitsaanbod of ligging in de kijker</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="home__content-block">
        <div className="home__content-block__wrapper">
          <div className="home__content-block__wrapper__info">
            <div className="home__content-block__wrapper__info__text">
              <h3>Hoe geraak jij op je nieuwe job?</h3>
              <p>
                Ben je op zoek naar een nieuwe job? Hier vind je vacatures die bij
                je passen én makkelijk bereikbaar zijn. We richten ons op jobs in
                jouw omgeving. Duurzaamheid staat centraal: kies je voor een job
                dichtbij, dan heb je geen auto nodig. We helpen je door aan te geven
                hoe je met de fiets, te voet of het openbaar vervoer op je nieuwe
                job geraakt. Probeer onze app daarom vandaag nog.
              </p>
            </div>
            <div className="home__content-block__wrapper__info__image">
              <Icon
                className="home__content-block__wrapper__info__image__icon" 
                icon="/img/illustrations/buildings.svg"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="home__content-block">
        <div className="home__content-block__wrapper">
          <div className="home__content-block__wrapper__info">
            <div className="home__content-block__wrapper__info__image">
              <Icon
                className="home__content-block__wrapper__info__image__icon" 
                icon="/img/illustrations/icons.svg"
              />
            </div>
            <div className="home__content-block__wrapper__info__text">
              <h3>Een extra tool voor jobcoaches</h3>
              <p>
                Voor heel wat werkzoekenden is hun ideale job vaak dichterbij dan ze
                zelf denken. Toch vormt mobiliteit soms een drempel. Ben jij een
                jobcoach, arbeidsbemiddelaar of toeleider? Dan help NaarJobs je om
                die mobiliteitsdrempels te overwinnen.
              </p>
              <p>
                Liever met de fiets, bus of trein? Bekijk samen met je klant de bereikbaarheid
                van verschillende vacatures, ontdek slimme vervoersopties en vind snel vacatures
                voor elke werkzoekende.
              </p>
              <p>
                Superhandig! Registreer je vandaag nog gratis op dit platform!
              </p>
              <div className="home__intro__buttons">
                <Button
                  className="home__intro__buttons__button"
                  onClick={() => {
                    navigate('/jobcoach');
                  }}
                >
                  Meer info voor jobcoaches
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="home__content-block">
        <div className="home__content-block__wrapper">
          <div className="home__content-block__wrapper__info">
            <div className="home__content-block__wrapper__info__text">
              <h3>Nieuw talent voor je bedrijf is dichterbij dan je denkt</h3>
              <p>
                Op NaarJobs vinden mensen hun ideale job op basis van bereikbaarheid.
                Biedt jouw bedrijf of organisatie fietsleasing, een treinabonnement of
                andere mobiliteitsvoordelen aan? Met NaarJobs zet je die extra’s
                in de verf en trek jij nieuw talent aan. Steeds meer mensen zoeken trouwens
                een job in de buurt, waardoor de bereikbaarheid van jouw organisatie een
                belangrijke plus is.
              </p>
              <p>De troeven van NaarJobs:</p>
              <IconList
                className="home__content-block__wrapper__info__text__list"
                items={[
                  {
                    key: 0,
                    content: 'Jouw vacatures komen automatisch op het platform',
                    iconProps: { icon: faArrowsMinimize },
                  },
                  {
                    key: 1, 
                    content: 'Toon de ligging van je bedrijf en de mobiliteitsopties in de buurt',
                    iconProps: { icon: faBus }, 
                  },
                  {
                    key: 2,
                    content: 'Supersnel en gratis registreren om de mobiliteitsvoordelen van jouw organisatie in de verf te zetten',
                    iconProps: { icon: '/img/icons/euro-sign-solid-strike-through.svg' },
                  },
                  {
                    key: 3,
                    content: 'Een extra, innovatief kanaal om nieuw talent te rekruteren',
                    iconProps: { icon: faUsers },
                  },
                ]}
              />
              <p>
                Vergroot je zichtbaarheid, zet de mobiliteitsvoordelen van jouw organisatie
                op NaarJobs en maak die match!
              </p>
              <div className="home__intro__buttons">
                <Button
                  className="home__intro__buttons__button"
                  onClick={() => {
                    navigate('/werkgever');
                  }}
                >
                  Meer info voor werkgevers
                </Button>
              </div>
            </div>
            <div className="home__content-block__wrapper__info__image">
              <Icon
                className="home__content-block__wrapper__info__image__icon" 
                icon="/img/icons/mobiliteitsaanbod.svg"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="home__content-block">
        <div className="home__content-block__wrapper">
          <div className="home__content-block__wrapper__info">
            <div className="home__content-block__wrapper__info__image">
              <Icon
                className="home__content-block__wrapper__info__image__icon" 
                icon="/img/illustrations/email-blue.svg"
              />
            </div>
            <div className="home__content-block__wrapper__info__text">
              <h3>Contact</h3>
              <p>
                Zit je met een vraag of wil je graag met ons samenwerken? 
                {' Stuur een e-mail naar '}
                <a href="mailto:info@naarjobs.be">
                  info@naarjobs.be
                </a>
                {' of gebruik het contactformulier.'}
              </p>
              <Button
                className="home__content-block__wrapper__info__text__button"
                onClick={() => {
                  dispatch(setShowContactModal(true));
                }}
              >
                Contact
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Partners />
      <Footer />
    </div>
  );
}

export default Home;
